import { useCallback, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

/**
 * Универсальный хук для синхронизации фильтров с URL search params.
 *
 * Поддерживает типы: string, string[] (через запятую), boolean.
 *
 * Пример:
 *   const { filters, setFilter, resetFilters, hasActiveFilters } = useUrlFilters({
 *     search:   { type: 'string' },
 *     status:   { type: 'array' },
 *     partner:  { type: 'boolean' },
 *     country:  { type: 'array' },
 *   })
 *
 *   filters.search   // string | undefined
 *   filters.status   // string[] | undefined
 *   filters.partner  // boolean | undefined
 *
 *   setFilter('search', 'test@email.com')
 *   setFilter('status', ['active', 'pending'])
 *   setFilter('partner', true)
 *   setFilter('search', undefined)  // removes from URL
 */

type FilterType = 'string' | 'array' | 'boolean'

type FilterConfig = Record<string, { type: FilterType; default?: any; pagination?: boolean }>

type FilterValues<T extends FilterConfig> = {
  [K in keyof T]?: T[K]['type'] extends 'string'
    ? string
    : T[K]['type'] extends 'array'
    ? string[]
    : T[K]['type'] extends 'boolean'
    ? boolean
    : never
}

export function useUrlFilters<T extends FilterConfig>(config: T) {
  const [searchParams, setSearchParams] = useSearchParams()

  const filters = useMemo(() => {
    const result: Record<string, any> = {}

    for (const [key, conf] of Object.entries(config)) {
      const raw = searchParams.get(key)

      if (raw === null) {
        if (conf.default !== undefined) {
          result[key] = conf.default
        }
        continue
      }

      switch (conf.type) {
        case 'string':
          result[key] = raw
          break
        case 'array':
          result[key] = raw.split(',').filter(Boolean)
          break
        case 'boolean':
          result[key] = raw === 'true'
          break
      }
    }

    return result as FilterValues<T>
  }, [searchParams])

  const setFilter = useCallback(
    (key: keyof T & string, value: any) => {
      const next = new URLSearchParams(window.location.search)

      if (value === undefined || value === null || value === '') {
        next.delete(key)
      } else if (Array.isArray(value)) {
        if (value.length === 0) {
          next.delete(key)
        } else {
          next.set(key, value.join(','))
        }
      } else {
        next.set(key, String(value))
      }

      setSearchParams(next, { replace: true })
    },
    [setSearchParams],
  )

  const setFilters = useCallback(
    (values: Partial<FilterValues<T>>) => {
      const next = new URLSearchParams(window.location.search)

      for (const [key, value] of Object.entries(values)) {
        if (value === undefined || value === null || value === '') {
          next.delete(key)
        } else if (Array.isArray(value)) {
          if (value.length === 0) {
            next.delete(key)
          } else {
            next.set(key, value.join(','))
          }
        } else {
          next.set(key, String(value))
        }
      }

      setSearchParams(next, { replace: true })
    },
    [setSearchParams],
  )

  const filterKeys = useMemo(
    () => Object.keys(config).filter((key) => !config[key].pagination),
    [],
  )

  const resetFilters = useCallback(() => {
    const next = new URLSearchParams(window.location.search)
    for (const key of filterKeys) {
      next.delete(key)
    }
    setSearchParams(next, { replace: true })
  }, [setSearchParams, filterKeys])

  const hasActiveFilters = useMemo(() => {
    return filterKeys.some((key) => searchParams.has(key))
  }, [searchParams, filterKeys])

  return { filters, setFilter, setFilters, resetFilters, hasActiveFilters }
}
